// Search helpers and hand-tuned aliases for the reaction/compose picker.
//
// ---- Adding an emoji -------------------------------------------------------
//
// The complete list is generated from Unicode/CLDR by `npm run emoji:update`.
// Add entries here only to preserve a familiar shortcode or supply extra
// colloquial search terms; the generated list merges these overrides by glyph.
//
//   text       the emoji character itself, as typed
//   shortcode  its familiar name, without colons. Must be unique.
//   terms      extra search words — synonyms worth finding it by ("lol" for
//              joy, "omg" for open_mouth). The picker already matches the
//              shortcode and the character itself, so terms are purely for
//              aliases. Lowercase; matched case-insensitively as substrings.
//              terms[0] doubles as the label in the cell's hover tooltip.
//
// Keep overrides roughly grouped (faces, hands, hearts, symbols).
//
// ---- Before renaming or removing one ---------------------------------------
//
// Curated `shortcode` values are also legacy favorite identifiers. Current
// favorites use the Unicode glyph itself, but loading still understands the
// old "unicode:<shortcode>" keys. Keep curated names stable so those saved
// favorites continue to resolve. Reactions themselves use `text`.
//
// Note some emoji are ZWJ sequences of several code points (😵‍💫 is
// U+1F635 U+200D U+1F4AB). Paste them whole; don't hand-assemble.
//
// ---- On the trailing U+FE0F ------------------------------------------------
//
// Most characters here are emoji by default and need no variation selector; a
// trailing U+FE0F on one of those is a paste artifact worth removing, because
// it changes the reaction name sent to the server without changing how the
// entry looks, so the same reaction lands in two buckets across clients.
//
// A few characters default to monochrome text instead (Emoji_Presentation=No)
// and are unrecognisable that way — 🕯️ U+1F56F is an unreadable outline bare.
// Those keep the selector; the split-bucket cost is the lesser problem. The
// emoji.js test enforces exactly this split, so a stray selector still fails.

// ---- matching ---------------------------------------------------------------
//
// Shared by the picker and the compose ":" autocomplete so a query finds the
// same emoji in both. Both take entries shaped { shortcode, text?, terms? },
// which covers the unicode list below and the instance's custom emoji alike.

// Entries matching `query` by shortcode, by the character itself, or by one of
// the search terms. An empty query matches everything.
export function filterEmojiList(emojis, query) {
  if (!query) return emojis;
  const q = query.toLowerCase();
  return emojis.filter((e) => {
    if (e.shortcode && e.shortcode.toLowerCase().includes(q)) return true;
    if (e.text && e.text.includes(query)) return true;   // unicode match
    if (Array.isArray(e.terms) && e.terms.some((t) => t.toLowerCase().includes(q))) return true;
    return false;
  });
}

// How well an entry matches, lowest first. A one-character query matches a
// large slice of the full list, so ordering is what makes a capped menu
// usable: what you typed in full beats what merely starts with it, which beats
// a mid-word hit, which beats a synonym-only hit (":lol" finding joy).
export function emojiMatchRank(e, q) {
  const shortcode = String(e.shortcode || "").toLowerCase();
  if (shortcode === q) return 0;
  if (shortcode.startsWith(q)) return 1;
  if (shortcode.includes(q)) return 2;
  return 3;                                  // matched on a search term only
}

// Sorts in place, best match first. `q` must already be lowercased.
export function rankEmojiMatches(list, q) {
  return list.sort((a, b) =>
    emojiMatchRank(a, q) - emojiMatchRank(b, q) ||
    a.shortcode.length - b.shortcode.length ||   // the more specific name
    a.shortcode.localeCompare(b.shortcode));
}

// The six shown in the picker's favorites row before the user customises it.
// Each must be a shortcode present in the generated, merged list.
export const DEFAULT_EMOJI_FAVORITES = ["fire", "skull", "100", "joy_cat", "heart", "eyes"];

export const CURATED_UNICODE_EMOJIS = [
  // faces
  { text: "😀", shortcode: "grinning",     terms: ["grinning", "smile", "happy"] },
  { text: "😃", shortcode: "smiley",       terms: ["smiley", "smile", "happy"] },
  { text: "😊", shortcode: "blush",        terms: ["blush", "smile", "happy"] },
  { text: "😆", shortcode: "laughing",     terms: ["laughing", "laugh", "haha"] },
  { text: "😂", shortcode: "joy",          terms: ["joy", "lol", "laugh", "tears"] },
  { text: "🤣", shortcode: "rofl",         terms: ["rofl", "laugh", "rolling"] },
  { text: "😹", shortcode: "joy_cat",      terms: ["joy_cat", "cat", "laugh", "tears"] },
  { text: "😅", shortcode: "sweat_smile",  terms: ["sweat_smile", "phew", "relief", "nervous"] },
  { text: "😉", shortcode: "wink",         terms: ["wink", "flirt"] },
  { text: "😎", shortcode: "sunglasses",   terms: ["sunglasses", "cool", "deal_with_it"] },
  { text: "😍", shortcode: "heart_eyes",   terms: ["heart_eyes", "love", "crush"] },
  { text: "🥰", shortcode: "smiling_hearts", terms: ["smiling_hearts", "love", "adore"] },
  { text: "😘", shortcode: "kissing_heart", terms: ["kissing_heart", "kiss", "love"] },
  { text: "🙃", shortcode: "upside_down",  terms: ["upside_down", "silly"] },
  { text: "🙄", shortcode: "roll_eyes",    terms: ["roll_eyes", "eyeroll", "whatever"] },
  { text: "😬", shortcode: "grimacing",    terms: ["grimacing", "yikes", "awkward"] },
  { text: "🤔", shortcode: "thinking",     terms: ["thinking", "hmm", "think"] },
  { text: "🤨", shortcode: "raised_eyebrow", terms: ["raised_eyebrow", "suspicious", "doubt"] },
  { text: "😐", shortcode: "neutral",      terms: ["neutral", "meh", "blank"] },
  { text: "😵‍💫", shortcode: "spiral_eyes",   terms: ["spiral_eyes", "spiral", "dizzy", "confused", "woozy"] },
  { text: "🥴", shortcode: "woozy",        terms: ["woozy", "drunk", "tipsy"] },
  { text: "🤯", shortcode: "exploding_head", terms: ["exploding_head", "mind_blown", "wow"] },
  { text: "😮", shortcode: "open_mouth",   terms: ["open_mouth", "wow", "surprise", "omg"] },
  { text: "😱", shortcode: "scream",       terms: ["scream", "fear", "shocked"] },
  { text: "😭", shortcode: "sob",          terms: ["sob", "cry", "sad", "bawling"] },
  { text: "😢", shortcode: "cry",          terms: ["cry", "sad", "tear"] },
  { text: "🥺", shortcode: "pleading",     terms: ["pleading", "please", "puppy"] },
  { text: "😤", shortcode: "triumph",      terms: ["triumph", "huff", "steam", "smug"] },
  { text: "😡", shortcode: "rage",         terms: ["rage", "angry", "mad"] },
  { text: "🤢", shortcode: "nauseated",    terms: ["nauseated", "sick", "gross"] },
  { text: "🤮", shortcode: "vomiting",     terms: ["vomiting", "puke", "sick"] },
  { text: "😴", shortcode: "sleeping",     terms: ["sleeping", "sleep", "zzz", "bored"] },
  { text: "🥱", shortcode: "yawning",      terms: ["yawning", "yawn", "tired", "bored"] },
  { text: "🫠", shortcode: "melting",      terms: ["melting", "melt"] },
  { text: "🫡", shortcode: "saluting",     terms: ["saluting", "salute", "respect", "o7"] },
  { text: "🤡", shortcode: "clown",        terms: ["clown", "fool"] },
  { text: "😈", shortcode: "smiling_imp",  terms: ["smiling_imp", "devil", "evil", "mischief"] },
  { text: "👽", shortcode: "alien",        terms: ["alien", "ufo", "extraterrestrial"] },
  { text: "🤖", shortcode: "robot",        terms: ["robot", "bot", "ai"] },
  { text: "🗿", shortcode: "moai",         terms: ["moai", "moyai", "stone", "statue", "deadpan"] },
  { text: "🐸", shortcode: "frog",         terms: ["frog", "toad"] },
  { text: "💀", shortcode: "skull",        terms: ["skull", "dead", "die"] },
  { text: "☠️", shortcode: "skull_crossbones", terms: ["skull_crossbones", "poison", "danger", "pirate"] },
  { text: "👻", shortcode: "ghost",        terms: ["ghost", "boo", "spooky"] },
  { text: "🙂", shortcode: "slight_smile",   terms: ["slight_smile", "smile", "happy", "ok"] },
  { text: "😏", shortcode: "smirk",          terms: ["smirk", "smug", "flirt"] },
  { text: "😼", shortcode: "smirk_cat",      terms: ["smirk_cat", "cat", "smug"] },
  { text: "😒", shortcode: "unamused",       terms: ["unamused", "meh", "annoyed"] },
  { text: "🙁", shortcode: "frowning",       terms: ["frowning", "sad", "upset"] },
  { text: "😞", shortcode: "disappointed",   terms: ["disappointed", "sad", "let_down"] },
  { text: "😟", shortcode: "worried",        terms: ["worried", "anxious", "concern"] },
  { text: "😳", shortcode: "flushed",        terms: ["flushed", "blush", "embarrassed"] },
  { text: "😌", shortcode: "relieved",       terms: ["relieved", "content", "calm"] },
  { text: "🤤", shortcode: "drooling",       terms: ["drooling", "drool", "want", "hungry"] },
  { text: "😇", shortcode: "innocent",       terms: ["innocent", "halo", "angel"] },
  { text: "🧐", shortcode: "monocle",        terms: ["monocle", "inspecting", "skeptical", "detective"] },
  { text: "🤓", shortcode: "nerd",           terms: ["nerd", "geek", "smart"] },
  { text: "🥸", shortcode: "disguise",       terms: ["disguise", "costume", "sneaky", "fake"] },
  { text: "🤪", shortcode: "zany",           terms: ["zany", "crazy", "wild"] },
  { text: "🥳", shortcode: "partying",       terms: ["partying", "party", "celebrate"] },
  { text: "🥶", shortcode: "cold_face",      terms: ["cold_face", "cold", "freezing", "chill"] },
  { text: "🥵", shortcode: "hot_face",       terms: ["hot_face", "hot", "sweating", "heat"] },
  { text: "🤒", shortcode: "sick",           terms: ["sick", "thermometer", "ill"] },
  { text: "🤧", shortcode: "sneezing",       terms: ["sneezing", "achoo", "cold", "ill"] },
  { text: "🤫", shortcode: "shush",          terms: ["shush", "quiet", "secret", "shh"] },
  { text: "🤐", shortcode: "zipper_mouth",   terms: ["zipper_mouth", "mute", "sealed", "shut"] },
  { text: "🤥", shortcode: "lying",          terms: ["lying", "liar", "pinocchio"] },
  { text: "😚", shortcode: "kissing_closed_eyes", terms: ["kissing_closed_eyes", "kiss", "love"] },
  { text: "😻", shortcode: "heart_eyes_cat", terms: ["heart_eyes_cat", "cat", "love"] },
  { text: "💋", shortcode: "kiss",           terms: ["kiss", "lips", "love", "smooch"] },
  { text: "🙈", shortcode: "see_no_evil",    terms: ["see_no_evil", "monkey", "hide", "embarrassed"] },
  { text: "🙉", shortcode: "hear_no_evil",   terms: ["hear_no_evil", "monkey", "listen", "ignore"] },
  { text: "🙊", shortcode: "speak_no_evil",  terms: ["speak_no_evil", "monkey", "secret", "quiet"] },

  // hands and people
  { text: "👍", shortcode: "+1",           terms: ["+1", "thumbsup", "thumbs", "up", "like", "yes", "agree", "ok"] },
  { text: "👎", shortcode: "-1",           terms: ["-1", "thumbsdown", "down", "dislike", "no"] },
  { text: "👌", shortcode: "ok_hand",      terms: ["ok_hand", "okay", "perfect"] },
  { text: "👏", shortcode: "clap",         terms: ["clap", "applause", "congrats"] },
  { text: "🙏", shortcode: "pray",         terms: ["pray", "please", "thanks"] },
  { text: "🤝", shortcode: "handshake",    terms: ["handshake", "deal", "agree"] },
  { text: "💪", shortcode: "muscle",       terms: ["muscle", "strong", "flex"] },
  { text: "🤌", shortcode: "pinched_fingers", terms: ["pinched_fingers", "italian", "gesture"] },
  { text: "✌️", shortcode: "v",             terms: ["victory", "peace", "two"] },
  { text: "🤘", shortcode: "metal",        terms: ["metal", "rock", "horns"] },
  { text: "👋", shortcode: "wave",         terms: ["wave", "hello", "bye"] },
  { text: "🫂", shortcode: "hugging",      terms: ["hugging", "hug", "comfort"] },
  { text: "🤷", shortcode: "shrug",        terms: ["shrug", "dunno", "whatever"] },
  { text: "🤦", shortcode: "facepalm",     terms: ["facepalm", "smh", "ugh"] },
  { text: "👀", shortcode: "eyes",         terms: ["eyes", "look", "watch"] },
  { text: "🧠", shortcode: "brain",        terms: ["brain", "smart", "galaxy"] },
  { text: "✊", shortcode: "fist",           terms: ["fist", "power", "solidarity", "fight"] },
  { text: "👊", shortcode: "punch",          terms: ["punch", "fist_bump", "hit", "bro"] },
  { text: "🙌", shortcode: "raised_hands",   terms: ["raised_hands", "praise", "celebrate"] },
  { text: "👐", shortcode: "open_hands",     terms: ["open_hands", "hug", "welcome"] },
  { text: "🫶", shortcode: "heart_hands",    terms: ["heart_hands", "love", "care"] },
  { text: "🫰", shortcode: "fingers_heart",  terms: ["fingers_heart", "snap", "fingers", "heart"] },
  { text: "🤞", shortcode: "crossed_fingers", terms: ["crossed_fingers", "luck", "hope", "fingers_crossed"] },
  { text: "🤟", shortcode: "love_you",       terms: ["love_you", "ily", "rock_on"] },
  { text: "🤙", shortcode: "call_me",        terms: ["call_me", "phone", "shaka"] },
  { text: "🖖", shortcode: "vulcan",         terms: ["vulcan", "spock", "live_long"] },
  { text: "🤚", shortcode: "raised_back",    terms: ["raised_back", "stop", "high_five"] },
  { text: "👈", shortcode: "point_left",     terms: ["point_left", "left", "that_way"] },
  { text: "👉", shortcode: "point_right",    terms: ["point_right", "right", "this", "look"] },
  { text: "👆", shortcode: "point_up_2",     terms: ["point_up_2", "up", "above"] },
  { text: "👇", shortcode: "point_down",     terms: ["point_down", "down", "below"] },
  { text: "🫵", shortcode: "point_at_viewer", terms: ["point_at_viewer", "you", "index"] },
  { text: "💅", shortcode: "nail_care",      terms: ["nail_care", "manicure", "fabulous", "sass"] },
  { text: "🤳", shortcode: "selfie",         terms: ["selfie", "me", "camera"] },
  { text: "👶", shortcode: "baby",           terms: ["baby", "babies", "infant", "child"] },
  { text: "🤰", shortcode: "pregnant_woman", terms: ["pregnant_woman", "pregnant", "pregnancy", "mother"] },
  { text: "🫃", shortcode: "pregnant_man",   terms: ["pregnant_man", "pregnant", "pregnancy", "father"] },
  { text: "🫄", shortcode: "pregnant_person", terms: ["pregnant_person", "pregnant", "pregnancy"] },
  { text: "👮", shortcode: "police_officer", terms: ["police_officer", "police", "cop", "officer", "law"] },

  // hearts
  { text: "❤️", shortcode: "heart",         terms: ["heart", "love", "red"] },
  { text: "💔", shortcode: "broken_heart", terms: ["broken_heart", "heartbreak", "sad"] },
  { text: "💕", shortcode: "two_hearts",   terms: ["two_hearts", "love"] },
  { text: "💜", shortcode: "purple_heart", terms: ["purple_heart", "heart", "love"] },
  { text: "💙", shortcode: "blue_heart",   terms: ["blue_heart", "heart", "love"] },
  { text: "💚", shortcode: "green_heart",  terms: ["green_heart", "heart", "love"] },
  { text: "🧡", shortcode: "orange_heart",   terms: ["orange_heart", "heart", "love"] },
  { text: "💛", shortcode: "yellow_heart",   terms: ["yellow_heart", "heart", "love"] },
  { text: "🤍", shortcode: "white_heart",    terms: ["white_heart", "heart", "love"] },
  { text: "🖤", shortcode: "black_heart",    terms: ["black_heart", "heart", "love", "goth"] },
  { text: "🤎", shortcode: "brown_heart",    terms: ["brown_heart", "heart", "love"] },
  { text: "💖", shortcode: "sparkling_heart", terms: ["sparkling_heart", "heart", "love", "sparkle"] },
  { text: "💗", shortcode: "growing_heart",  terms: ["growing_heart", "heart", "love", "grow"] },
  { text: "💓", shortcode: "beating_heart",  terms: ["beating_heart", "heart", "love", "pulse"] },
  { text: "💞", shortcode: "revolving_hearts", terms: ["revolving_hearts", "heart", "love"] },
  { text: "💘", shortcode: "cupid",          terms: ["cupid", "heart", "love", "arrow"] },
  { text: "💝", shortcode: "gift_heart",     terms: ["gift_heart", "heart", "love", "present"] },
  { text: "💌", shortcode: "love_letter",    terms: ["love_letter", "love", "letter", "mail"] },

  // symbols and objects
  { text: "💢", shortcode: "anger",        terms: ["anger", "vein", "correction"] },
  { text: "🔥", shortcode: "fire",         terms: ["fire", "lit", "hot"] },
  { text: "💯", shortcode: "100",          terms: ["100", "hundred", "perfect", "score"] },
  { text: "✨", shortcode: "sparkles",     terms: ["sparkles", "shine", "magic"] },
  { text: "🕯️", shortcode: "candle",        terms: ["candle", "light", "vigil", "ritual", "manifest"] },
  { text: "🔮", shortcode: "crystal_ball",  terms: ["crystal_ball", "fortune", "future", "magic", "manifest"] },
  { text: "💥", shortcode: "boom",         terms: ["boom", "explosion", "bang"] },
  { text: "🎉", shortcode: "tada",         terms: ["tada", "party", "celebrate"] },
  { text: "🎂", shortcode: "birthday",     terms: ["birthday", "cake"] },
  { text: "🚀", shortcode: "rocket",       terms: ["rocket", "launch", "space"] },
  { text: "🍿", shortcode: "popcorn",      terms: ["popcorn", "drama", "watching"] },
  { text: "💤", shortcode: "zzz",          terms: ["zzz", "sleep", "boring"] },
  { text: "⭐", shortcode: "star",          terms: ["star", "favorite"] },
  { text: "✅", shortcode: "white_check_mark", terms: ["check", "done", "yes"] },
  { text: "❌", shortcode: "x",             terms: ["x", "no", "wrong", "cancel"] },
  { text: "❓", shortcode: "question",      terms: ["question", "what", "huh"] },
  { text: "❗", shortcode: "exclamation",   terms: ["exclamation", "important", "warning"] },
  { text: "⚠️", shortcode: "warning",       terms: ["warning", "caution"] },
  { text: "💩", shortcode: "poop",         terms: ["poop", "shit"] },
  { text: "💣", shortcode: "bomb",          terms: ["bomb", "explosion", "boom"] },

  // religion symbols
  { text: "🛐", shortcode: "place_of_worship", terms: ["place_of_worship", "religion", "prayer"] },
  { text: "⚛️", shortcode: "atom_symbol",     terms: ["atom_symbol", "atheism", "science", "religion"] },
  { text: "🕉️", shortcode: "om",              terms: ["om", "hindu", "hinduism", "religion"] },
  { text: "✡️", shortcode: "star_of_david",  terms: ["star_of_david", "david", "jewish", "judaism", "religion", "star"] },
  { text: "☸️", shortcode: "wheel_of_dharma", terms: ["wheel_of_dharma", "buddhist", "buddhism", "religion"] },
  { text: "☯️", shortcode: "yin_yang",        terms: ["yin_yang", "tao", "taoism", "religion"] },
  { text: "✝️", shortcode: "latin_cross",     terms: ["latin_cross", "cross", "christian", "christianity", "religion"] },
  { text: "☦️", shortcode: "orthodox_cross",  terms: ["orthodox_cross", "cross", "orthodox", "christian", "religion"] },
  { text: "☪️", shortcode: "star_and_crescent", terms: ["star_and_crescent", "crescent", "islam", "muslim", "religion"] },
  { text: "☮️", shortcode: "peace_symbol",    terms: ["peace_symbol", "peace", "pacifism"] },
  { text: "🕎", shortcode: "menorah",          terms: ["menorah", "jewish", "judaism", "hanukkah", "religion"] },
  { text: "🔯", shortcode: "six_pointed_star", terms: ["six_pointed_star", "dotted", "fortune", "religion", "star"] },
  { text: "🪯", shortcode: "khanda",           terms: ["khanda", "sikh", "sikhism", "religion"] },

  // tools and tech
  { text: "🔧", shortcode: "wrench",        terms: ["wrench", "tool", "fix", "repair", "spanner"] },
  { text: "🔨", shortcode: "hammer",        terms: ["hammer", "tool", "nail", "build"] },
  { text: "🔗", shortcode: "link",          terms: ["link", "url", "chain"] },
  { text: "🔒", shortcode: "lock",          terms: ["lock", "secure", "private", "closed"] },
  { text: "🔓", shortcode: "unlock",        terms: ["unlock", "open", "free"] },
  { text: "🔑", shortcode: "key",           terms: ["key", "password", "secret"] },
  { text: "💡", shortcode: "bulb",          terms: ["bulb", "idea", "light", "tip"] },
  { text: "📌", shortcode: "pushpin",       terms: ["pushpin", "pin", "note", "mark"] },
  { text: "📍", shortcode: "round_pushpin", terms: ["round_pushpin", "pin", "location", "here"] },
  { text: "📎", shortcode: "paperclip",     terms: ["paperclip", "attach", "clip"] },
  { text: "📝", shortcode: "memo",          terms: ["memo", "note", "write", "edit"] },
  { text: "💻", shortcode: "computer",      terms: ["computer", "laptop", "code", "dev"] },
  { text: "📱", shortcode: "phone",         terms: ["phone", "mobile", "cell"] },
  { text: "📷", shortcode: "camera",        terms: ["camera", "photo", "pic"] },
  { text: "📺", shortcode: "tv",            terms: ["tv", "television", "watch"] },
  { text: "🎧", shortcode: "headphones",    terms: ["headphones", "music", "listen", "audio"] },

  // money and awards
  { text: "💰", shortcode: "moneybag",      terms: ["moneybag", "money", "cash", "rich"] },
  { text: "💸", shortcode: "money_wings",   terms: ["money_wings", "money", "spent", "gone"] },
  { text: "💎", shortcode: "gem",           terms: ["gem", "diamond", "rare", "shiny"] },
  { text: "🏆", shortcode: "trophy",        terms: ["trophy", "win", "first", "champion"] },
  { text: "🥇", shortcode: "first_place",   terms: ["first_place", "gold", "win", "best"] },

  // games and activity
  { text: "🎯", shortcode: "dart",          terms: ["dart", "bullseye", "target", "hit"] },
  { text: "🎲", shortcode: "dice",          terms: ["dice", "roll", "random", "gamble"] },
  { text: "🎮", shortcode: "game",          terms: ["game", "gaming", "controller", "play"] },
  { text: "🎳", shortcode: "bowling",       terms: ["bowling", "strike", "game"] },

  // weather and elements
  { text: "⚡", shortcode: "zap",           terms: ["zap", "lightning", "fast", "energy"] },
  { text: "💧", shortcode: "droplet",       terms: ["droplet", "water", "drop"] },
  { text: "💦", shortcode: "sweat_drops",   terms: ["sweat_drops", "splash", "water"] },
  { text: "🌊", shortcode: "ocean",         terms: ["ocean", "wave", "sea", "water"] },
  { text: "🌈", shortcode: "rainbow",       terms: ["rainbow", "pride", "color"] },

  // sky and moon phases
  { text: "🌙", shortcode: "moon",          terms: ["moon", "night", "crescent"] },
  { text: "🌑", shortcode: "new_moon",      terms: ["new_moon", "moon", "dark", "phase"] },
  { text: "🌒", shortcode: "waxing_crescent", terms: ["waxing_crescent", "moon", "phase"] },
  { text: "🌓", shortcode: "first_quarter", terms: ["first_quarter", "moon", "half", "phase"] },
  { text: "🌔", shortcode: "waxing_gibbous", terms: ["waxing_gibbous", "moon", "phase"] },
  { text: "🌕", shortcode: "full_moon",     terms: ["full_moon", "moon", "phase", "luna"] },
  { text: "🌖", shortcode: "waning_gibbous", terms: ["waning_gibbous", "moon", "phase"] },
  { text: "🌗", shortcode: "last_quarter",  terms: ["last_quarter", "moon", "half", "phase"] },
  { text: "🌘", shortcode: "waning_crescent", terms: ["waning_crescent", "moon", "phase"] },
  { text: "🌚", shortcode: "new_moon_face", terms: ["new_moon_face", "moon", "dark", "shady", "sus"] },
  { text: "🌝", shortcode: "full_moon_face", terms: ["full_moon_face", "moon", "smug", "bright"] },
  { text: "🌛", shortcode: "first_quarter_face", terms: ["first_quarter_face", "moon", "face"] },
  { text: "🌜", shortcode: "last_quarter_face", terms: ["last_quarter_face", "moon", "face"] },
  { text: "☀️", shortcode: "sun",           terms: ["sun", "sunny", "day", "bright"] },
  { text: "🌞", shortcode: "sun_face",      terms: ["sun_face", "sun", "sunny", "smile"] },

  // nature and animals
  { text: "🌍", shortcode: "earth",         terms: ["earth", "globe", "world"] },
  { text: "🌸", shortcode: "cherry_blossom", terms: ["cherry_blossom", "flower", "pink", "bloom"] },
  { text: "🌹", shortcode: "rose",          terms: ["rose", "flower", "love"] },
  { text: "🍀", shortcode: "clover",        terms: ["clover", "luck", "lucky", "green"] },
  { text: "🌵", shortcode: "cactus",        terms: ["cactus", "plant", "desert"] },
  { text: "🌲", shortcode: "tree",          terms: ["tree", "pine", "forest"] },
  { text: "🍃", shortcode: "leaf",          terms: ["leaf", "leaves", "wind", "nature"] },
  { text: "🍄", shortcode: "mushroom",      terms: ["mushroom", "shroom", "fungus"] },
  { text: "🐵", shortcode: "monkey_face",   terms: ["monkey_face", "monkey", "ape", "primate", "neuron_activation", "meme"] },
  { text: "🐒", shortcode: "monkey",        terms: ["monkey", "ape", "primate", "animal"] },
  { text: "🐱", shortcode: "cat",           terms: ["cat", "kitten", "meow"] },
  { text: "🐶", shortcode: "dog",           terms: ["dog", "puppy", "woof"] },
  { text: "🐷", shortcode: "pig",           terms: ["pig", "oink"] },
  { text: "🐧", shortcode: "penguin",       terms: ["penguin", "bird", "linux"] },
  { text: "🐍", shortcode: "snake",         terms: ["snake", "serpent", "snek"] },
  { text: "🐢", shortcode: "turtle",        terms: ["turtle", "slow", "tortoise"] },
  { text: "🐝", shortcode: "bee",           terms: ["bee", "bug", "honey"] },
  { text: "🐙", shortcode: "octopus",       terms: ["octopus", "tentacle", "sea"] },
  { text: "🦀", shortcode: "crab",          terms: ["crab", "shellfish", "cancer"] },
  { text: "🦄", shortcode: "unicorn",       terms: ["unicorn", "magic", "horse"] },
  { text: "🐉", shortcode: "dragon",        terms: ["dragon", "mythical", "fire"] },

  // food and drink
  { text: "🍕", shortcode: "pizza",         terms: ["pizza", "food", "slice"] },
  { text: "🍔", shortcode: "burger",        terms: ["burger", "food", "hamburger"] },
  { text: "🌮", shortcode: "taco",          terms: ["taco", "food", "mexican"] },
  { text: "🍟", shortcode: "fries",         terms: ["fries", "food", "potato"] },
  { text: "🍆", shortcode: "eggplant",      terms: ["eggplant", "aubergine", "veg"] },
  { text: "🍑", shortcode: "peach",         terms: ["peach", "fruit", "butt"] },
  { text: "🍌", shortcode: "banana",        terms: ["banana", "fruit"] },
  { text: "🥑", shortcode: "avocado",       terms: ["avocado", "avo", "guac"] },
  { text: "🍼", shortcode: "baby_bottle",   terms: ["baby_bottle", "baby", "bottle", "milk", "infant"] },
  { text: "🍩", shortcode: "donut",         terms: ["donut", "doughnut", "food", "sweet"] },
  { text: "🍪", shortcode: "cookie",        terms: ["cookie", "food", "sweet"] },
  { text: "🍫", shortcode: "chocolate",     terms: ["chocolate", "food", "candy", "sweet"] },
  { text: "🍦", shortcode: "icecream",      terms: ["icecream", "ice_cream", "sweet", "dessert"] },
  { text: "🧂", shortcode: "salt",          terms: ["salt", "salty", "sodium"] },
  { text: "☕", shortcode: "coffee",        terms: ["coffee", "cafe", "morning", "caffeine"] },
  { text: "🍵", shortcode: "tea",           terms: ["tea", "drink", "green"] },
  { text: "🍺", shortcode: "beer",          terms: ["beer", "drink", "ale", "pub"] },
  { text: "🍻", shortcode: "beers",         terms: ["beers", "drink", "cheers", "party"] },
  { text: "🍷", shortcode: "wine",          terms: ["wine", "drink", "glass", "drunk"] },

  // time
  { text: "⌛", shortcode: "hourglass",     terms: ["hourglass", "time", "wait", "done"] },
  { text: "⏳", shortcode: "hourglass_flow", terms: ["hourglass_flow", "time", "waiting", "sands"] },
  { text: "⏰", shortcode: "alarm_clock",   terms: ["alarm_clock", "time", "wake", "morning"] },
];

let unicodeEmojiPromise = null;

// The full search index lives in a separate generated module. Production
// builds keep it in a lazy chunk, so visiting chan-fe does not make everyone
// download the full dataset; opening the picker or typing ':' loads it once.
export function loadUnicodeEmojis() {
  if (!unicodeEmojiPromise) {
    unicodeEmojiPromise = import("./emoji-data.js").then((module) => module.UNICODE_EMOJIS);
  }
  return unicodeEmojiPromise;
}
